import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from "react";
import { useDataContext } from "../../hub/data-context-provider.js";
export function RemoteFilePicker({ hostId, hostKind = "pty", workspaceId, initialPath = "~", separator = "/", onSelect, }) {
    const ctx = useDataContext();
    const [current, setCurrent] = useState(initialPath);
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reloadTick, setReloadTick] = useState(0);
    const [mkdirOpen, setMkdirOpen] = useState(false);
    const [mkdirName, setMkdirName] = useState("");
    const [mkdirSubmitting, setMkdirSubmitting] = useState(false);
    // Reset to home when the host changes — paths don't carry across.
    // `initialPath` is intentionally NOT a dep: it seeds the initial
    // `current` via useState above, and the parent's workingDir-tracking
    // (driven by onSelect after each list resolves) would otherwise
    // re-trigger this effect on every navigation and clobber any open
    // mkdir form.
    useEffect(() => {
        setCurrent(initialPath);
        setResult(null);
        setError(null);
        setMkdirOpen(false);
        setMkdirName("");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hostId]);
    useEffect(() => {
        let cancelled = false;
        if (hostKind === "runtime" && !workspaceId) {
            setError("RemoteFilePicker: workspaceId is required for runtime hosts.");
            setLoading(false);
            return () => undefined;
        }
        setLoading(true);
        setError(null);
        const list = hostKind === "runtime" && workspaceId
            ? ctx.hub.listRuntimeHostFs(hostId, current, workspaceId)
            : ctx.hub.listHostFs(hostId, current);
        list
            .then((r) => {
            if (cancelled)
                return;
            setResult(r);
            // Keep the parent's working-dir field in sync with whatever
            // the picker is currently showing, so the form submits the
            // directory the user is looking at — not whatever was typed
            // into the text field before they navigated. Callers that
            // only want a one-shot "user chose X" event will see one
            // event per navigation, which is fine for the agent-create
            // flow (the only consumer today).
            onSelect(r.path);
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        })
            .finally(() => {
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, hostId, hostKind, workspaceId, current, reloadTick, onSelect]);
    const effectiveSeparator = result?.separator ?? separator;
    const resolvedPath = result?.path ?? current;
    const goUp = useCallback(() => {
        const path = resolvedPath;
        if (path === effectiveSeparator || /^[A-Za-z]:[\\/]?$/.test(path))
            return;
        const stripped = path.endsWith(effectiveSeparator) ? path.slice(0, -1) : path;
        const idx = stripped.lastIndexOf(effectiveSeparator);
        if (idx < 0)
            return;
        const parent = idx === 0 ? effectiveSeparator : stripped.slice(0, idx);
        setCurrent(parent);
    }, [resolvedPath, effectiveSeparator]);
    const descend = useCallback((entry) => {
        if (!entry.isDir)
            return;
        const joined = resolvedPath.endsWith(effectiveSeparator)
            ? `${resolvedPath}${entry.name}`
            : `${resolvedPath}${effectiveSeparator}${entry.name}`;
        setCurrent(joined);
    }, [resolvedPath, effectiveSeparator]);
    const submitMkdir = useCallback(async () => {
        const trimmed = mkdirName.trim();
        if (!trimmed || mkdirSubmitting)
            return;
        setMkdirSubmitting(true);
        setError(null);
        try {
            const hub = ctx.hub;
            const mkdirFn = hostKind === "runtime" ? hub.createRuntimeHostFsDir : hub.createHostFsDir;
            if (typeof mkdirFn !== "function") {
                throw new Error("The fs mkdir endpoint is not available on this Hub yet. Update the deployment to enable in-picker mkdir.");
            }
            if (hostKind === "runtime" && !workspaceId) {
                throw new Error("RemoteFilePicker: workspaceId is required for runtime hosts.");
            }
            // Hub forwards `path` verbatim to the daemon's fs:mkdir RPC; the
            // join has to happen here. Mirror the separator handling that
            // `descend` uses so paths like `~` (no trailing separator) and
            // `C:\` (already trailing) both compose correctly (KTK-296).
            const target = resolvedPath.endsWith(effectiveSeparator)
                ? `${resolvedPath}${trimmed}`
                : `${resolvedPath}${effectiveSeparator}${trimmed}`;
            const made = hostKind === "runtime"
                ? await hub.createRuntimeHostFsDir(hostId, target, workspaceId)
                : await hub.createHostFsDir(hostId, target);
            // Navigate into the new directory so the user can `Use this directory`
            // immediately if that's what they wanted.
            setCurrent(made.path);
            setMkdirOpen(false);
            setMkdirName("");
            setReloadTick((t) => t + 1);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : String(err));
        }
        finally {
            setMkdirSubmitting(false);
        }
    }, [ctx.hub, hostId, hostKind, workspaceId, mkdirName, mkdirSubmitting, resolvedPath, effectiveSeparator]);
    return (_jsxs("div", { style: {
            background: "var(--bg-inset, #0d0e11)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            overflow: "hidden",
        }, children: [_jsxs("div", { style: {
                    padding: "6px 10px",
                    fontSize: 11,
                    color: "var(--text-muted)",
                    background: "var(--bg-tertiary)",
                    borderBottom: "1px solid var(--border)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                }, children: [_jsx("button", { type: "button", onClick: goUp, style: tinyBtn("muted"), children: "\u2191 Up" }), _jsx("span", { style: {
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontFamily: "monospace",
                            color: "var(--text-primary)",
                        }, title: resolvedPath, children: resolvedPath }), _jsx("button", { type: "button", onClick: () => setMkdirOpen((o) => !o), style: tinyBtn("muted"), title: "Create a new directory here", children: "+ new dir" }), _jsx("button", { type: "button", onClick: () => onSelect(resolvedPath), style: tinyBtn("accent"), children: "Use this directory" })] }), mkdirOpen && (_jsxs("div", { style: {
                    padding: "8px 10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    background: "var(--bg-tertiary)",
                    borderBottom: "1px solid var(--border)",
                }, children: [_jsxs("div", { style: { display: "flex", gap: 6, alignItems: "center" }, children: [_jsx("input", { autoFocus: true, value: mkdirName, onChange: (e) => setMkdirName(e.target.value), onKeyDown: (e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        void submitMkdir();
                                    }
                                    else if (e.key === "Escape") {
                                        setMkdirOpen(false);
                                        setMkdirName("");
                                    }
                                }, placeholder: "new-directory-name", style: {
                                    flex: 1,
                                    padding: "4px 8px",
                                    fontSize: 12,
                                    fontFamily: "monospace",
                                    background: "var(--bg-primary)",
                                    color: "var(--text-primary)",
                                    border: "1px solid var(--border)",
                                    borderRadius: 4,
                                    outline: "none",
                                } }), _jsx("button", { type: "button", onClick: () => void submitMkdir(), disabled: mkdirSubmitting || !mkdirName.trim(), style: tinyBtn(mkdirSubmitting || !mkdirName.trim() ? "muted" : "accent"), children: mkdirSubmitting ? "creating…" : "create" }), _jsx("button", { type: "button", onClick: () => {
                                    setMkdirOpen(false);
                                    setMkdirName("");
                                }, style: tinyBtn("muted"), children: "cancel" })] }), !mkdirName.trim() && (_jsx("div", { style: {
                            fontSize: 11,
                            color: "var(--text-muted)",
                            paddingLeft: 2,
                        }, children: "Folder name required." }))] })), _jsxs("div", { style: { maxHeight: 200, overflowY: "auto" }, children: [loading && (_jsx("div", { style: { padding: 10, fontSize: 12, color: "var(--text-muted)" }, children: "Loading\u2026" })), error && (_jsx("div", { style: { padding: 10, fontSize: 12, color: "var(--error)" }, children: error })), !loading && !error && result && result.entries.length === 0 && (_jsx("div", { style: { padding: 10, fontSize: 12, color: "var(--text-muted)" }, children: "Empty directory." })), !loading && !error && result && result.entries.length > 0 && (_jsx("ul", { style: { listStyle: "none", margin: 0, padding: 0 }, children: result.entries.map((entry) => (_jsx("li", { children: _jsxs("button", { type: "button", onClick: () => descend(entry), disabled: !entry.isDir, style: {
                                    width: "100%",
                                    textAlign: "left",
                                    padding: "4px 12px",
                                    background: "transparent",
                                    border: "none",
                                    color: entry.isDir ? "var(--text-primary)" : "var(--text-muted)",
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    cursor: entry.isDir ? "pointer" : "default",
                                }, children: [entry.isDir ? "📁" : "📄", " ", entry.name] }) }, entry.name))) }))] })] }));
}
function tinyBtn(variant) {
    return {
        background: "transparent",
        border: "none",
        fontSize: 11,
        padding: "3px 6px",
        cursor: "pointer",
        fontFamily: "inherit",
        color: variant === "accent" ? "var(--accent)" : "var(--text-secondary)",
    };
}
//# sourceMappingURL=RemoteFilePicker.js.map